import React, { useState } from 'react';
import {
  Home,
  BookOpen,
  Users,
  Shield,
  Link as LinkIcon,
  FileCheck,
  Server,
  Brain,
  Cpu,
  Sparkles,
} from 'lucide-react';

import TrustPortalOverview from './screens/TrustPortalOverview';
import PurposeAndContext from './screens/PurposeAndContext';
import SubProviderScope from './screens/SubProviderScope';
import TSGSecurityPosture from './screens/TSGSecurityPosture';
import TrustPortalQuickReference from './screens/TrustPortalQuickReference';
import RequestingFullAuditReports from './screens/RequestingFullAuditReports';
import VendorProfileLink from './screens/VendorProfileLink';
import MistralPlaceholder from './screens/MistralPlaceholder';

// ──────────────────────────────────────────────────────────────────────────
// TrustPortalLayout — state-based internal navigation for the 10 screens.
// ──────────────────────────────────────────────────────────────────────────
//
// Why state-based rather than sub-routes:
//   Matches the existing Admin.tsx pattern in the codebase (one parent
//   route, internal state drives which sub-screen is rendered). This keeps
//   App.tsx changes minimal and avoids having to thread 10 sub-routes
//   through the router.
//
// The nav rail on the left lists all 10 screens grouped into two sections:
// "Overview" and "Vendor Profiles". Clicking an item swaps the content
// area; the help icon on each rendered screen opens its own slide-out
// panel with the appropriate help text from trustPortalHelpText.ts.

export type TrustPortalScreenKey =
  | 'overview'
  | 'purpose'
  | 'scope'
  | 'security-posture'
  | 'quick-reference'
  | 'audit-reports'
  | 'railway'
  | 'anthropic'
  | 'openai'
  | 'mistral';

interface NavItem {
  key: TrustPortalScreenKey;
  label: string;
  icon: React.ElementType;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Trust Portal',
    items: [
      { key: 'overview', label: 'Overview', icon: Home },
      { key: 'purpose', label: 'Purpose and Context', icon: BookOpen },
      { key: 'scope', label: 'Sub-Provider Scope', icon: Users },
      { key: 'security-posture', label: 'TSG Security Posture', icon: Shield },
      { key: 'quick-reference', label: 'Quick Reference', icon: LinkIcon },
      { key: 'audit-reports', label: 'Audit Report Pathways', icon: FileCheck },
    ],
  },
  {
    title: 'Vendor Profiles',
    items: [
      { key: 'railway', label: 'Railway', icon: Server },
      { key: 'anthropic', label: 'Anthropic', icon: Brain },
      { key: 'openai', label: 'OpenAI', icon: Cpu },
      { key: 'mistral', label: 'Mistral (Placeholder)', icon: Sparkles },
    ],
  },
];

const TrustPortalLayout: React.FC = () => {
  const [active, setActive] = useState<TrustPortalScreenKey>('overview');

  const renderScreen = () => {
    switch (active) {
      case 'overview':
        return <TrustPortalOverview onNavigate={setActive} />;
      case 'purpose':
        return <PurposeAndContext />;
      case 'scope':
        return <SubProviderScope onNavigate={setActive} />;
      case 'security-posture':
        return <TSGSecurityPosture />;
      case 'quick-reference':
        return <TrustPortalQuickReference />;
      case 'audit-reports':
        return <RequestingFullAuditReports />;
      case 'railway':
        return <VendorProfileLink vendor="railway" />;
      case 'anthropic':
        return <VendorProfileLink vendor="anthropic" />;
      case 'openai':
        return <VendorProfileLink vendor="openai" />;
      case 'mistral':
        return <MistralPlaceholder />;
      default:
        return <TrustPortalOverview onNavigate={setActive} />;
    }
  };

  return (
    <div className="flex gap-6">
      {/* Left nav rail */}
      <aside className="w-64 shrink-0">
        <nav className="sticky top-4 space-y-6">
          {NAV_GROUPS.map(group => (
            <div key={group.title}>
              <div className="px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                {group.title}
              </div>
              <ul className="space-y-1">
                {group.items.map(item => {
                  const Icon = item.icon;
                  const isActive = active === item.key;
                  return (
                    <li key={item.key}>
                      <button
                        onClick={() => setActive(item.key)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 font-semibold border border-blue-200'
                            : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <Icon
                          className={`w-4 h-4 shrink-0 ${
                            isActive ? 'text-blue-700' : 'text-gray-500'
                          }`}
                        />
                        <span className="truncate">{item.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Content area */}
      <main className="flex-1 min-w-0">{renderScreen()}</main>
    </div>
  );
};

export default TrustPortalLayout;
